// frontend/src/features/ogdb2/components/MultiTokenInput.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * MultiTokenInput
 * - Supports paste from Excel (newlines/tabs/commas/spaces)
 * - Turns tokens into removable "chips"
 * - Enter adds current text as a token
 * - Backspace on empty removes last token
 * - Optional suggestions dropdown (from props)
 *
 * Props:
 *  - label: string
 *  - placeholder: string
 *  - tokens: string[]
 *  - onTokensChange(nextTokens: string[]): void
 *  - text: string
 *  - onTextChange(nextText: string): void
 *  - suggestions: string[] (optional)
 *  - disabled: boolean (optional)
 */

function parseTokensFromText(raw) {
  const s = String(raw || "");
  // split on newline, tab, comma, semicolon, space
  const parts = s.split(/[\n\r\t,; ]+/g).map((x) => x.trim()).filter(Boolean);

  // keep digits only tokens by default (since these are lease/RRC numbers)
  const cleaned = parts
    .map((x) => x.replace(/[^\d]/g, "")) // strip non-digits
    .map((x) => x.trim())
    .filter(Boolean);

  // de-dupe, preserve order
  const seen = new Set();
  const out = [];
  for (const t of cleaned) {
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

export default function MultiTokenInput({
  label,
  placeholder,
  tokens,
  onTokensChange,
  text,
  onTextChange,
  suggestions,
  disabled,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const toks = useMemo(
    () => (Array.isArray(tokens) ? tokens : []),
    [tokens]
  );

  const sug = useMemo(() => {
    const base = Array.isArray(suggestions) ? suggestions : [];
    const q = String(text || "").trim();
    if (!q) return [];
    const qDigits = q.replace(/[^\d]/g, "");
    if (!qDigits) return [];

    const lower = qDigits.toLowerCase();
    const filtered = base
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .filter((x) => x.toLowerCase().includes(lower))
      .filter((x) => !toks.includes(x));

    return filtered.slice(0, 8);
  }, [suggestions, text, toks]);

  useEffect(() => {
    if (!open) setActiveIdx(0);
  }, [open, text]);

  function addToken(rawToken) {
    const t = String(rawToken || "").replace(/[^\d]/g, "").trim();
    if (!t) return;
    if (toks.includes(t)) {
      onTextChange("");
      return;
    }
    onTokensChange([...toks, t]);
    onTextChange("");
  }

  function removeToken(t) {
    onTokensChange(toks.filter((x) => x !== t));
  }

  function clearAll() {
    onTokensChange([]);
    onTextChange("");
  }

  function handlePaste(e) {
    const clip = e.clipboardData?.getData("text");
    if (!clip) return;
    const parsed = parseTokensFromText(clip);
    if (!parsed.length) return;

    e.preventDefault();

    // merge tokens
    const seen = new Set(toks);
    const merged = [...toks];
    for (const t of parsed) {
      if (seen.has(t)) continue;
      seen.add(t);
      merged.push(t);
    }
    onTokensChange(merged);
    onTextChange("");
    setOpen(false);
  }

  function commitTopSuggestionOrText() {
    if (sug.length) {
      addToken(sug[Math.max(0, Math.min(activeIdx, sug.length - 1))]);
      setOpen(false);
      return;
    }
    addToken(text);
    setOpen(false);
  }

  return (
    <div className="relative">
      {label ? <label className="block text-xs text-gray-500 mb-1">{label}</label> : null}

      <div
        className={[
          "w-full rounded-xl border border-gray-200 bg-white px-2 py-2 flex flex-wrap items-center gap-2",
          disabled ? "opacity-60" : "",
        ].join(" ")}
        onClick={() => inputRef.current?.focus()}
      >
        {toks.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
            title="Click x to remove"
          >
            <span className="font-mono text-gray-800">{t}</span>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={(e) => {
                e.stopPropagation();
                removeToken(t);
              }}
              aria-label={`Remove ${t}`}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          className="min-w-[180px] flex-1 outline-none border-0 text-sm px-2 py-1"
          placeholder={toks.length ? "" : (placeholder || "")}
          value={text || ""}
          disabled={disabled}
          onChange={(e) => {
            onTextChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // small delay so suggestion click registers
            setTimeout(() => setOpen(false), 150);
          }}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !String(text || "").trim() && toks.length) {
              // remove last token
              onTokensChange(toks.slice(0, toks.length - 1));
              return;
            }

            if (e.key === "Enter") {
              e.preventDefault();
              commitTopSuggestionOrText();
              return;
            }

            if (e.key === "ArrowDown" && sug.length) {
              e.preventDefault();
              setActiveIdx((i) => Math.min(i + 1, sug.length - 1));
              return;
            }

            if (e.key === "ArrowUp" && sug.length) {
              e.preventDefault();
              setActiveIdx((i) => Math.max(i - 1, 0));
              return;
            }

            if (e.key === "Escape") {
              setOpen(false);
              return;
            }
          }}
        />

        {toks.length ? (
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-800 px-2"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            disabled={disabled}
            title="Clear all"
          >
            Clear
          </button>
        ) : null}
      </div>

      {open && sug.length ? (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          {sug.map((s, idx) => (
            <button
              key={s}
              type="button"
              className={[
                "w-full text-left px-3 py-2 text-sm hover:bg-gray-50",
                idx === activeIdx ? "bg-gray-50" : "",
              ].join(" ")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                addToken(s);
                setOpen(false);
                inputRef.current?.focus();
              }}
            >
              <span className="font-mono">{s}</span>
            </button>
          ))}
        </div>
      ) : null}

      {toks.length ? (
        <div className="mt-1 text-[11px] text-gray-500">
          Paste a column from Excel (newlines ok). Enter adds a token. {toks.length} selected.
        </div>
      ) : (
        <div className="mt-1 text-[11px] text-gray-500">
          Tip: paste a column from Excel (newlines ok) — it’ll become a list automatically.
        </div>
      )}
    </div>
  );
}

